// Очередь исходящих: отправка переживает офлайн и обрыв (§4 задания Р-09).
//
// Порядок именно такой: сначала запись в локальную базу, потом сеть. Пользователь видит своё сообщение
// сразу и видит, что с ним происходит, — а не пустой экран, пока крутится запрос. Повтор идёт с
// нарастающей задержкой: сеть, которая только что не ответила, редко отвечает через 50 мс.

import { createClient, type Transport } from "@connectrpc/connect";
import { MessageService } from "@yofis/proto/yofis/v1/message_pb";

import type { Clock } from "./platform.ts";
import { systemClock } from "./platform.ts";
import type { LocalStore } from "./store.ts";
import { toLocalMessage } from "./sync.ts";

/** Что отправляем; сегодня это текст, дальше добавятся вложения. */
interface SendPayload {
  chatId: string;
  text: string;
  replyToId?: string;
}

export interface OutboxOptions {
  transport: Transport;
  store: LocalStore;
  clock?: Clock;
  /** Первая задержка повтора, мс; дальше удваивается до `maxBackoffMs`. */
  backoffMs?: number;
  maxBackoffMs?: number;
  /** Кого дёрнуть, когда локальные данные изменились. */
  onChange?: () => void;
}

/** Отправка с очередью и повторами. */
export class Outbox {
  readonly #transport: Transport;
  readonly #store: LocalStore;
  readonly #clock: Clock;
  readonly #backoffMs: number;
  readonly #maxBackoffMs: number;
  readonly #onChange: () => void;

  constructor(options: OutboxOptions) {
    this.#transport = options.transport;
    this.#store = options.store;
    this.#clock = options.clock ?? systemClock;
    this.#backoffMs = options.backoffMs ?? 1_000;
    this.#maxBackoffMs = options.maxBackoffMs ?? 60_000;
    this.#onChange = options.onChange ?? (() => {});
  }

  /**
   * Кладёт сообщение в базу и пытается отправить. Ошибку сети наружу не выбрасывает: сообщение уже
   * в базе и в очереди, UI покажет его неотправленным — это не отказ, а состояние.
   */
  async send(payload: SendPayload, me: string): Promise<string> {
    const clientMsgId = crypto.randomUUID();
    const now = this.#clock.now();
    this.#store.enqueue(
      {
        clientMsgId,
        chatId: payload.chatId,
        payload: JSON.stringify(payload),
        attempts: 0,
        nextAttemptAt: now,
        lastError: "",
      },
      {
        id: `local:${clientMsgId}`,
        chatId: payload.chatId,
        clientMsgId,
        sender: me,
        // Текстовое сообщение (`MESSAGE_KIND_TEXT` = 1): черновик рисуется так же, как отправленное.
        kind: 1,
        text: payload.text,
        createdAt: now,
        editedAt: undefined,
        deleted: false,
        unreadable: false,
        state: "pending",
        replyToId: payload.replyToId,
      },
    );
    this.#onChange();
    await this.#attempt(clientMsgId, payload);
    return clientMsgId;
  }

  /** Отправляет всё, чему пришёл срок. Вызывается при возврате сети и по таймеру. */
  async flush(): Promise<void> {
    for (const entry of this.#store.due(this.#clock.now())) {
      await this.#attempt(entry.clientMsgId, JSON.parse(entry.payload) as SendPayload, entry.attempts);
    }
  }

  /** Сколько сообщений ждёт отправки. */
  get pending(): number {
    return this.#store.pendingCount();
  }

  async #attempt(clientMsgId: string, payload: SendPayload, attempts = 0): Promise<void> {
    const messages = createClient(MessageService, this.#transport);
    try {
      const res = await messages.sendMessage({
        chatId: payload.chatId,
        clientMsgId,
        text: payload.text,
        replyToId: payload.replyToId ?? "",
      });
      if (res.message !== undefined) {
        // Повтор с тем же client_msg_id сервер считает тем же сообщением (сценарий idempotent_send),
        // поэтому повторная отправка после неясного обрыва безопасна и дублей не создаёт.
        this.#store.replacePending(clientMsgId, toLocalMessage(res.message));
      }
    } catch (err) {
      const delay = Math.min(this.#backoffMs * 2 ** attempts, this.#maxBackoffMs);
      this.#store.markFailed(clientMsgId, describe(err), this.#clock.now() + delay);
    }
    this.#onChange();
  }
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
